import React, { useEffect, useState } from 'react';

/**
 * CognitoCallback — Handles the OAuth callback from Cognito Hosted UI.
 *
 * When Cognito redirects to /callback?code=XXXXX, this component:
 *   1. Extracts the authorization code
 *   2. Exchanges it for tokens via Cognito's /oauth2/token endpoint
 *   3. Stores tokens in localStorage
 *   4. Redirects to the main app
 *
 * Used for both localhost:3000 (dev) and plug-n-play-infra.ai/distributors (prod).
 */

const COGNITO_DOMAIN = 'https://us-east-1jlrxymqam.auth.us-east-1.amazoncognito.com';
const CLIENT_ID = '7van0bphlk7e8ri4g08ldb6kt7';

const CognitoCallback = ({ onLoginSuccess }) => {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const err = params.get('error');

    if (err) {
      setStatus('error');
      setError(params.get('error_description') || err);
      return;
    }

    if (!code) {
      // No code — redirect to login
      triggerLogin();
      return;
    }

    // Exchange code for tokens
    const host = window.location.hostname;
    const port = window.location.port;
    let redirectUri;
    if (host === 'localhost' || host === '127.0.0.1') {
      redirectUri = `http://localhost:${port || '3000'}/callback`;
    } else if (host.startsWith('staging.')) {
      redirectUri = `https://staging.plug-n-play-infra.ai/callback`;
    } else {
      redirectUri = `https://plug-n-play-infra.ai/callback`;
    }

    const tokenUrl = `${COGNITO_DOMAIN}/oauth2/token`;
    const body = `grant_type=authorization_code&client_id=${CLIENT_ID}&code=${code}&redirect_uri=${encodeURIComponent(redirectUri)}`;

    fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          setStatus('error');
          setError(data.error_description || data.error);
          return;
        }

        // Store tokens
        if (data.access_token) localStorage.setItem('pnp_access_token', data.access_token);
        if (data.id_token) localStorage.setItem('pnp_id_token', data.id_token);
        if (data.refresh_token) localStorage.setItem('pnp_refresh_token', data.refresh_token);

        // Decode user info from ID token
        try {
          const payload = JSON.parse(atob(data.id_token.split('.')[1]));
          const email = payload.email || payload['cognito:username'] || '';
          setUserEmail(email);
          localStorage.setItem('pnp_user_email', email);
          localStorage.setItem('pnp_user_sub', payload.sub || '');
        } catch { /* ignore */ }

        setStatus('success');

        // Redirect back to where the user came from (staging vs prod)
        // The signin page stores the origin path before redirecting to Cognito
        // Determine correct post-login path based on user role
        const storedPath = localStorage.getItem('pnp_post_login_path');
        localStorage.removeItem('pnp_post_login_path');
        let postLoginPath = storedPath || '/pnp_main';

        // If no stored path, route based on email domain
        if (!storedPath) {
          try {
            const payload = JSON.parse(atob(data.id_token.split('.')[1]));
            const email = (payload.email || '').toLowerCase();
            const isAdmin = email.endsWith('@empirical-ais.com');
            postLoginPath = isAdmin ? '/pnp_main' : '/subscriber';
          } catch { /* fallback to /subscriber for safety */ postLoginPath = '/subscriber'; }
        }

        if (onLoginSuccess) {
          setTimeout(() => {
            window.location.href = postLoginPath;
          }, 1500);
        }
      })
      .catch(err => {
        setStatus('error');
        setError('Network error: ' + err.message);
      });
  }, [onLoginSuccess]);

  if (status === 'processing') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #1a237e, #283593)' }}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔄</div>
          <h2 style={{ margin: '0 0 8px', color: '#1a237e' }}>Authenticating...</h2>
          <p style={{ color: '#666', fontSize: '14px' }}>Exchanging authorization code for tokens</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #1a237e, #283593)' }}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>❌</div>
          <h2 style={{ margin: '0 0 8px', color: '#c62828' }}>Login Failed</h2>
          <p style={{ color: '#666', fontSize: '14px' }}>{error}</p>
          <button onClick={triggerLogin} style={{ marginTop: '16px', padding: '10px 24px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #1a237e, #283593)' }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', textAlign: 'center', maxWidth: '400px' }}>
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
        <h2 style={{ margin: '0 0 8px', color: '#2e7d32' }}>Welcome!</h2>
        <p style={{ color: '#666', fontSize: '14px' }}>{userEmail || 'User'}</p>
        <p style={{ color: '#999', fontSize: '12px' }}>Redirecting to dashboard...</p>
      </div>
    </div>
  );
};

function triggerLogin() {
  const host = window.location.hostname;
  const port = window.location.port;
  let redirectUri;
  if (host === 'localhost' || host === '127.0.0.1') {
    redirectUri = `http://localhost:${port || '3000'}/callback`;
  } else if (host.startsWith('staging.')) {
    redirectUri = 'https://staging.plug-n-play-infra.ai/callback';
  } else {
    redirectUri = 'https://plug-n-play-infra.ai/callback';
  }
  const loginUrl = `https://us-east-1jlrxymqam.auth.us-east-1.amazoncognito.com/login?client_id=7van0bphlk7e8ri4g08ldb6kt7&response_type=code&scope=openid+email+profile&redirect_uri=${encodeURIComponent(redirectUri)}`;
  window.location.href = loginUrl;
}

export { triggerLogin };
export default CognitoCallback;
